import { createHookContext } from '@clover-util/react-context';
import { useMerchant } from 'hooks/context/merchant';
import { getCurrentHours } from 'hooks/context/merchant/util';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { DateTime, Interval } from 'luxon';
import { Address, DeliveryEstimate } from 'olo-service/models';
import { useCallback } from 'react';
import { useMutation } from 'react-query';
import { useDelivery } from './context/Delivery';
import { useServiceType } from './context/serviceType';
import { ServiceType, ServiceTypeData } from './context/serviceType/models';
import { DeliveryAddressPayload, useGetLatestAddress } from './useApi/useAddress';
import { useColoAxiosInstance } from './useApi/useColoRequest';

export enum AddressErrors {
  ADDRESS_NOT_FOUND = 'No Address available',
  ADDRESS_NOT_VALID = 'Address not Valid'
}

export const DeliveryValidationException = function (address: DeliveryAddressPayload) {
  return {
    message: AddressErrors.ADDRESS_NOT_VALID,
    address
  };
};

export const { useDeliveryValidation, DeliveryValidationProvider } = createHookContext('DeliveryValidation', () => {
  const { merchant, service } = useMerchant();
  const timezone = merchant?.timezone || 'local';
  const currentHours = getCurrentHours(timezone, service);
  const navigate = useSmartNavigate();
  const { update: updateServiceType, updateTypeMetadata } = useServiceType();
  const { setDeliveryAddress, setDeliveryMetadata, setDefaultAddressHasBeenLoaded } = useDelivery();
  const serverDefaultAddress = useGetLatestAddress(false);

  const axios = useColoAxiosInstance();

  const changeGlobalStateToDelivery = useCallback((address: Address, metadata: ServiceTypeData) => {
    setDeliveryAddress(address);
    setDeliveryMetadata(metadata);
    updateTypeMetadata(metadata);
  }, [setDeliveryAddress, setDeliveryMetadata, updateTypeMetadata]);

  const validateAddress = useMutation(async (address: Address) => {
    let deliveryEstimate: DeliveryEstimate | undefined = undefined;
    try {
      const response = await axios.post<DeliveryEstimate>(`${merchant?.merchantUuid}/deliveries/estimate`, { address });
      deliveryEstimate = response.data;
    } catch (e: any) {
      // react-query catches and sends this to onError methods
      // this is also logged by console.error
      // if its an issue in production we can silence react-query logs
      // https://stackoverflow.com/questions/65570462/how-do-i-mute-react-query-console-errors-when-a-promise-rejects
      throw new Error(e.response?.data?.message);
    }

    if (currentHours) {
      const isAfterClosing = Interval.fromDateTimes(currentHours?.startTime, currentHours?.endTime).isBefore(
        DateTime.fromMillis(deliveryEstimate.pickupTime).setZone(timezone)
      );

      if (isAfterClosing) {
        throw new Error('Pickup time after closing');
      }
    }

    return deliveryEstimate;
  });

  const asyncValidateAndSetDefaultAddress = useCallback(
    (addressPayload: DeliveryAddressPayload, options?: { replacePath?: boolean }, serviceType?: string) => {
      validateAddress.mutate(addressPayload, {
        onSuccess: () => {
          const metadata = {
            isContactlessDelivery: addressPayload.preference ? addressPayload.preference === 'contactless' : true,
            note: addressPayload.note,
          };
          changeGlobalStateToDelivery(addressPayload, metadata);
          updateServiceType(ServiceType.DELIVERY, metadata);
          setDefaultAddressHasBeenLoaded(true);
        },
        onError: (error) => {
          updateServiceType(serviceType ? serviceType : ServiceType.PICKUP);
          setDefaultAddressHasBeenLoaded(true);
          const path = options?.replacePath ? '.address-error' : './address-error';
          navigate(path, { state: { mainAddress: addressPayload, error: (error as Error).message } });
        },
      });
    },
    [changeGlobalStateToDelivery, navigate, setDefaultAddressHasBeenLoaded, updateServiceType, validateAddress]
  );

  const getLatestAddressAndValidate = useCallback(async (isDeliveryFlow?: boolean) => {

    const response = await serverDefaultAddress.refetch();

    const address = response.data;

    if (!address) throw new Error(AddressErrors.ADDRESS_NOT_FOUND);

    return validateAddress.mutateAsync(address, {
      onSuccess: () => {
        const metadata = {
          isContactlessDelivery: address.preference ? address.preference === 'contactless' : true,
          note: address.note,
        };
        changeGlobalStateToDelivery(address, metadata);
        setDefaultAddressHasBeenLoaded(true);

        if (isDeliveryFlow) updateServiceType(ServiceType.DELIVERY, metadata);
      },
      onError: (error) => {
        setDefaultAddressHasBeenLoaded(true);
        throw DeliveryValidationException(address);
      },
    });
  },
  [changeGlobalStateToDelivery, serverDefaultAddress, setDefaultAddressHasBeenLoaded, updateServiceType, validateAddress]);

  return {
    ...validateAddress,
    asyncValidateAndSetDefaultAddress,
    changeGlobalStateToDelivery,
    getLatestAddressAndValidate
  };
});
